import React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Calendar, dateFnsLocalizer, EventProps, View } from 'react-big-calendar';
import { format, getDay, isValid, parse, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { BookingEvent, SlotField } from './bookingEvents';
import { useBookingEvents } from './useBookingEvents';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './ResponsesCalendar.css';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { 'en-US': enUS },
});

const CALENDAR_VIEWS: View[] = ['month', 'week', 'day', 'agenda'];
const DATE_PARAM_FORMAT = 'yyyy-MM-dd';

const readView = (raw: string | null): View =>
  CALENDAR_VIEWS.includes(raw as View) ? (raw as View) : 'month';

const readDate = (raw: string | null): Date => {
  const parsed = raw ? parse(raw, DATE_PARAM_FORMAT, new Date()) : null;
  return parsed && isValid(parsed) ? parsed : new Date();
};

const BookingEventLabel: React.FC<EventProps<BookingEvent>> = ({ event }) => (
  <span className="responses-calendar-event">
    <span className="responses-calendar-event-time">{format(event.start, 'h:mm a')}</span>
    {event.title}
  </span>
);

interface ResponsesCalendarProps {
  tenantId: string;
  formId: string;
  slotFields: SlotField[];
}

export const ResponsesCalendar: React.FC<ResponsesCalendarProps> = ({ tenantId, formId, slotFields }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { events, loading, error } = useBookingEvents(tenantId, formId, slotFields);
  const view = readView(searchParams.get('calView'));
  const date = readDate(searchParams.get('date'));

  const updateParams = (patch: Record<string, string>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(patch).forEach(([key, value]) => next.set(key, value));
        return next;
      },
      { replace: true },
    );

  const openResponse = (event: BookingEvent) =>
    navigate(`/tenant/${tenantId}/form-response/${event.responseId}`, {
      state: { backTo: `${location.pathname}${location.search}` },
    });

  if (error) {
    return <Typography className="responses-calendar-error">{error}</Typography>;
  }

  return (
    <Box className="responses-calendar">
      {loading && (
        <Box className="responses-calendar-loading">
          <CircularProgress size={28} />
        </Box>
      )}
      <Calendar<BookingEvent>
        className="responses-calendar-grid"
        localizer={localizer}
        culture="en-US"
        events={events}
        views={CALENDAR_VIEWS}
        view={view}
        date={date}
        onView={(next) => updateParams({ calView: next })}
        onNavigate={(next) => updateParams({ date: format(next, DATE_PARAM_FORMAT) })}
        popup
        tooltipAccessor={(event) => `${event.fieldLabel}: ${event.title}`}
        onSelectEvent={openResponse}
        components={{ event: BookingEventLabel }}
      />
    </Box>
  );
};
